import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/shared/ui';

import { PromptLibraryItemRow } from './saved-prompts-item-row';
import { PAGE_SIZE, type PromptLibraryItem } from './types';

type Props = {
  canCreatePrompt: boolean;
  createPromptDisabledReason?: string;
  isLoading?: boolean;
  items: PromptLibraryItem[];
  onSelectPrompt: (item: PromptLibraryItem) => void;
};

const PROMPT_LIST_MIN_HEIGHT = 71 * PAGE_SIZE;

export const SavedPromptsSection = (props: Props) => {
  const { items, isLoading = false, onSelectPrompt } = props;
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedPrompts = items.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE,
  );

  return (
    <div id="prompt-library-section" className="w-full pb-20 space-y-6">
      <div className="pl-4 text-sm font-semibold text-foreground">
        Saved Prompts
      </div>

      <div style={{ minHeight: `${PROMPT_LIST_MIN_HEIGHT}px` }}>
        {pagedPrompts.length > 0 ? (
          pagedPrompts.map((item, index) => (
            <div key={item.id}>
              <PromptLibraryItemRow
                onClick={() => onSelectPrompt(item)}
                item={item}
              />
              {index < pagedPrompts.length - 1 ? (
                <div className="h-px bg-border/60" />
              ) : null}
            </div>
          ))
        ) : isLoading ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm font-medium text-foreground">
              Loading saved prompts...
            </p>
          </div>
        ) : null}
      </div>

      {totalPages > 1 ? (
        <div className="flex items-center justify-center gap-1 py-3">
          <Button
            className="rounded-full size-8"
            disabled={currentPage === 0}
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            size="icon"
            type="button"
            variant="ghost"
          >
            <ChevronDown className="rotate-90 size-4" />
          </Button>
          <span className="text-xs text-center min-w-8 tabular-nums text-muted-foreground">
            {currentPage + 1} / {totalPages}
          </span>
          <Button
            className="rounded-full size-8"
            disabled={currentPage >= totalPages - 1}
            onClick={() =>
              setPage((prev) => Math.min(totalPages - 1, prev + 1))
            }
            size="icon"
            type="button"
            variant="ghost"
          >
            <ChevronDown className="-rotate-90 size-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
};
